import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlatformUsers } from '@/hooks/usePlatform';
import { formatDate, formatRelativeDate } from '@/lib/formatters';
import type { PlatformUserItem } from '@/types/platform';

const PER_PAGE = 25;

export function AdminUsersPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => setPage(1), [search]);

  const { data, isLoading } = usePlatformUsers({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
  });
  const rows = data?.data ?? [];
  const pagination = data?.meta.pagination;

  const columns: Column<PlatformUserItem>[] = [
    {
      key: 'email',
      header: 'Email',
      render: (u) => <span className="font-medium text-foreground">{u.email}</span>,
    },
    { key: 'name', header: 'Name', render: (u) => u.name },
    { key: 'role', header: 'Role', render: (u) => <span className="capitalize">{u.role.replace('_', ' ')}</span> },
    {
      key: 'org',
      header: 'Organization',
      render: (u) => (
        <Link to={`/admin/organizations/${u.organization_id}`} className="text-primary hover:underline">
          {u.organization_name}
        </Link>
      ),
    },
    { key: 'use_case', header: 'Use case', render: (u) => u.use_case || '—' },
    { key: 'created', header: 'Signed up', render: (u) => (u.created_at ? formatDate(u.created_at) : '—') },
    {
      key: 'login',
      header: 'Last login',
      render: (u) => (u.last_login_at ? formatRelativeDate(u.last_login_at) : 'Never'),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Platform Admin" title="Users" subtitle="Every user across all organizations." />

      <Input
        placeholder="Search by email…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="max-w-xs"
      />

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          keyExtractor={(u) => u.id}
          emptyMessage="No users match the current search."
        />
      )}

      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {rows.length} of {pagination.total} · page {pagination.page} of{' '}
            {pagination.total_pages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
